import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { RightsContext } from 'providers/RightsContext';
import { withTranslation } from 'react-i18next';
import OfferFeeCatalogListItemSummary from './OfferFeeCatalogListItemSummary';
import OfferFeeCatalogCardSummary from './OfferFeeCatalogCardSummary';
import OfferFeeCatalogListItemContainer from './OfferFeeCatalogListItemContainer';
import EuroSymbol from '@mui/icons-material/EuroSymbol';
import { Menu, MenuItem, Grid } from '@mui/material';
import { Button, Dialog, Typography } from '@totalsoft/rocket-ui';
import { IncomeSchemaNegociationStatus } from '../../../../../../constants/incomeSchema';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import CardNoResultsWrapper from 'components/layout/CardNoResultsWrapper';
import { CollapseCard } from '@totalsoft/rocket-ui';
import { BoxActionsWrapper, DetatchedAccordion } from 'components/common/CommonStyles';

export const OfferFeeCatalogCard = ({
  t,
  fees,
  numberOfFees,
  onPropertyChange,
  onSave,
  saving,
  editable,
  handleModifyData,
  onApprovalButtonClick,
  isDirty,
  schemaIsEligibleForNegotiation,
  onStartNegotiationButtonClick,
  onSendNegotiationForApprovalButtonClick,
  onConfirmNegotiationButtonClick,
  negotiationStatus,
  loaded,
  exchangeRates,
  isExpanded,
  onExpand
}) => {
  const [showStartNegotiationModal, setShowStartNegotiationModal] = useState(false);
  const [localNegotiationType, setLocalNegotiationType] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const { readOnlyFull, offerIncomeSchemaWrite, financialFeesNegotiate, isImage } = useContext(RightsContext);

  const handleMenuOpen = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStartNegotiationDocument = () => {
    handleStartNegotiation('document');
  };
  const handleStartNegotiationPartner = () => {
    handleStartNegotiation('partner');
  };
  const handleStartNegotiationGroup = () => {
    handleStartNegotiation('partner-group');
  };

  const handleStartNegotiation = negotiationType => {
    setShowStartNegotiationModal(true);
    setLocalNegotiationType(negotiationType);
  };

  const handleStartNegotiationPositive = () => {
    onStartNegotiationButtonClick(localNegotiationType);
    setShowStartNegotiationModal(false);
    setLocalNegotiationType('');
    handleMenuClose();
  };

  const handleStartNegotiationClose = () => {
    setShowStartNegotiationModal(false);
    setLocalNegotiationType('');
    handleMenuClose();
  };

  return (
    <CollapseCard
      id={'offerFeeCatalogCard'}
      icon={EuroSymbol}
      title={t('OfferEdit.FeeCatalog.Title')}
      subheader={<OfferFeeCatalogCardSummary negotiationStatus={negotiationStatus} numberOfFees={numberOfFees} />}
      hideSubheaderOnExpand
      expanded={isExpanded}
      onToggle={onExpand}
      toggleOnHeaderClick
    >
      <>
        {exchangeRates.map(exchangeRate => (
          <Typography key={exchangeRate.currency} mb={2}>
            {t('OfferEdit.FeeCatalog.ExchangeRate')}
            {exchangeRate.currency}
            {t('OfferEdit.FeeCatalog.ExchangeRateIs')}
            {exchangeRate.value}
          </Typography>
        ))}
        <CardNoResultsWrapper content={fees}>
          {fees.map((document, idx) => {
            return (
              <div key={idx}>
                <DetatchedAccordion
                  defaultExpanded={false}
                  title={
                    <OfferFeeCatalogListItemSummary
                      idx={idx}
                      fee={document}
                      schemaIsEligibleForNegotiation={schemaIsEligibleForNegotiation && financialFeesNegotiate}
                    />
                  }
                  content={
                    <OfferFeeCatalogListItemContainer
                      fee={document}
                      onPropertyChange={onPropertyChange}
                      saving={saving}
                      idx={idx}
                      editRight={!!editable}
                      loaded={loaded}
                      onApprovalButtonClick={onApprovalButtonClick}
                      schemaIsEligibleForNegotiation={schemaIsEligibleForNegotiation && financialFeesNegotiate}
                    />
                  }
                />
              </div>
            );
          })}
        </CardNoResultsWrapper>
        {!isImage && (
          <Grid container spacing={1} alignItems='center' mt={2}>
            {/*Negotiation buttons will have visibility logic */}
            {schemaIsEligibleForNegotiation && financialFeesNegotiate && loaded && (
              <Grid item>
                <Button
                  id={'offerFeeCatalogCard_startNegotiationBtn'}
                  aria-controls='negotiation-type-menu'
                  aria-haspopup='true'
                  disabled={readOnlyFull || saving || negotiationStatus === IncomeSchemaNegociationStatus.IN_PROGRESS}
                  onClick={handleMenuOpen}
                  endIcon={<ArrowDropDown />}
                >
                  {t('OfferEdit.FeeCatalog.StartNegotiation')}
                </Button>
                <Menu id='negotiation-type-menu' anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={handleMenuClose}>
                  <MenuItem onClick={handleStartNegotiationDocument}>{t('OfferEdit.FeeCatalog.DocumentNegotiation')}</MenuItem>
                  <MenuItem onClick={handleStartNegotiationPartner}>{t('OfferEdit.FeeCatalog.PartnerNegotiation')}</MenuItem>
                  <MenuItem onClick={handleStartNegotiationGroup}>{t('OfferEdit.FeeCatalog.PartnerGroupNegotiation')}</MenuItem>
                </Menu>
              </Grid>
            )}

            {schemaIsEligibleForNegotiation && financialFeesNegotiate && loaded && (
              <Grid item>
                <Button
                  id={'offerFeeCatalogCard_sendApprovalBtn'}
                  onClick={onSendNegotiationForApprovalButtonClick}
                  color='primary'
                  variant='contained'
                  disabled={readOnlyFull || saving || negotiationStatus !== IncomeSchemaNegociationStatus.IN_PROGRESS}
                >
                  {t('OfferEdit.FeeCatalog.SendApproval')}
                </Button>
              </Grid>
            )}

            {schemaIsEligibleForNegotiation && financialFeesNegotiate && loaded && (
              <Grid item>
                <Button
                  id={'offerFeeCatalogCard_confirmNegotiationBtn'}
                  onClick={onConfirmNegotiationButtonClick}
                  color='primary'
                  variant='contained'
                  disabled={readOnlyFull || saving || negotiationStatus !== IncomeSchemaNegociationStatus.FINALIZED}
                >
                  {t('OfferEdit.FeeCatalog.ConfirmNegotiation')}
                </Button>
              </Grid>
            )}

            {!schemaIsEligibleForNegotiation && loaded && (
              <Grid item>
                <Button
                  id={'offerFeeCatalogCard_modifyBtn'}
                  onClick={handleModifyData}
                  color='primary'
                  variant='contained'
                  disabled={readOnlyFull || saving || fees.length === 0 || !offerIncomeSchemaWrite || editable}
                >
                  {t('Change')}
                </Button>
              </Grid>
            )}

            {!schemaIsEligibleForNegotiation && loaded && (
              <Grid item>
                <Button
                  id={'offerFeeCatalogCard_saveBtn'}
                  onClick={onSave}
                  color='primary'
                  variant='contained'
                  disabled={!editable || !offerIncomeSchemaWrite || !isDirty}
                >
                  {saving ? t('Saving') + '...' : t('Save')}
                </Button>
              </Grid>
            )}
          </Grid>
        )}
        {showStartNegotiationModal && (
          <Dialog
            open={showStartNegotiationModal}
            onClose={handleStartNegotiationClose}
            aria-labelledby='fee-title'
            aria-describedby='fee-description'
            title={t('OfferEdit.FeeCatalog.StartNegotiationTitle')}
            content={t('OfferEdit.FeeCatalog.StartNegotiationConfirmation')}
            actions={
              <BoxActionsWrapper forDialog>
                <Button id={'warrantyDataCard_deleteAllAcceptBtn'} autoFocus onClick={handleStartNegotiationPositive}>
                  {t('OfferEdit.FeeCatalog.Yes')}
                </Button>
                <Button id={'warrantyDataCard_deleteAllRejectBtn'} onClick={handleStartNegotiationClose}>
                  {t('OfferEdit.FeeCatalog.No')}
                </Button>
              </BoxActionsWrapper>
            }
          />
        )}
      </>
    </CollapseCard>
  );
};

OfferFeeCatalogCard.propTypes = {
  fees: PropTypes.array.isRequired,
  numberOfFees: PropTypes.number,
  t: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  editable: PropTypes.bool.isRequired,
  handleModifyData: PropTypes.func.isRequired,
  isDirty: PropTypes.bool.isRequired,
  onApprovalButtonClick: PropTypes.func.isRequired,
  schemaIsEligibleForNegotiation: PropTypes.bool.isRequired,
  onStartNegotiationButtonClick: PropTypes.func.isRequired,
  onSendNegotiationForApprovalButtonClick: PropTypes.func.isRequired,
  onConfirmNegotiationButtonClick: PropTypes.func.isRequired,
  negotiationStatus: PropTypes.string,
  loaded: PropTypes.bool,
  exchangeRates: PropTypes.array.isRequired,
  onExpand: PropTypes.func.isRequired,
  isExpanded: PropTypes.bool.isRequired
};

export default withTranslation()(OfferFeeCatalogCard);
